import jsPDF from "jspdf";

// --- Modern Typography and Layout Constants ---
const FONTS = {
    primary: 'helvetica',
    secondary: 'helvetica'
};

const COLORS = {
    black: [0, 0, 0],
    darkGray: [64, 64, 64],
    mediumGray: [128, 128, 128],
    lightGray: [240, 240, 240],
    white: [255, 255, 255]
};

// --- RSA ID PDF Generator ---
export function generateRsaIdSummaryPdf(summaryData, date_from, date_to, skin = {}) {
    const doc = new jsPDF();
    let y = 58;
    const cardWidth = 175;
    const cardX = 17;

    // Colors and logo
    const accentColor = skin.primaryColor || [89, 255, 116];
    const headerBg = skin.headerBg || [255, 255, 255];
    const cardBg = skin.cardBg || [245, 246, 250];
    const borderColor = skin.borderColor || [200, 200, 200];
    const shadowColor = skin.shadowColor || [235, 235, 235];

    // --- Header Section ---
    doc.setFillColor(...headerBg);
    doc.rect(0, 0, 210, 38, 'F');
    doc.setTextColor(...accentColor);
    doc.setFontSize(24);
    doc.setFont(FONTS.primary, 'bold');
    doc.text("RSA ID Verification", 20, 22);

    // Logo
    doc.addImage(
        require('@/assets/images/new_black_logo2.png'),
        'PNG',
        158, 8, 32, 14
    );

    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.7);
    doc.line(20, 30, 190, 30);

    doc.setFontSize(13);
    doc.setTextColor(64, 64, 64);
    doc.setFont(FONTS.secondary, 'bold');
    doc.text("South African Identity Verification Summary", 20, 36);

    const period = date_from && date_to
        ? `${formatDate(date_from)} – ${formatDate(date_to)}`
        : "All dates";

    doc.setFontSize(11);
    doc.setTextColor(128, 128, 128);
    doc.setFont(FONTS.secondary, 'normal');
    doc.text(`Report Period: ${period}`, 20, 44);
    doc.text(`Total Records: ${summaryData.length}`, 150, 44);

    // --- Summary Cards ---
    summaryData.forEach((item, idx) => {
        if (y > 230) {
            doc.addPage();
            y = 20;
        }

        const res = item?.data?.response || {};

        const fields = {
            index: idx + 1,
            name: `${res.FirstName || ""} ${res.Surname || ""}`.trim() || "Name not provided",
            dob: res.BirthDate || "N/A",
            id: res.IdNumber || "N/A",
            status: res.Status || "N/A",
            marital: res.MaritalStatus || "N/A",
            smartCard: res.SmartCardIssued ? "Yes" : "No",
            birthPlace: res.BirthPlaceCountry || "N/A",
            issueDate: formatDate(res.IDIssueDate) || "N/A",
            note: res.Message || ""
        };

        doc.setFillColor(...shadowColor);
        doc.roundedRect(cardX + 1, y + 1, cardWidth, 60, 4, 4, 'F');
        
        doc.setFillColor(...cardBg);
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(cardX, y, cardWidth, 60, 4, 4, 'FD');

        doc.setFillColor(...accentColor);
        doc.roundedRect(cardX, y, cardWidth, 10, 4, 4, 'F');
        doc.rect(cardX, y + 6, cardWidth, 4, 'F');

        doc.setFontSize(11);
        doc.setFont(FONTS.primary, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Applicant ${fields.index}: ${fields.name}`, cardX + 6, y + 7);

        // Content section
        const contentY = y + 16;
        doc.setFontSize(9);
        doc.setTextColor(64, 64, 64);

        // Left column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("ID Number:", cardX + 6, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.id, cardX + 28, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Date of Birth:", cardX + 6, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.dob, cardX + 28, contentY + 6);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Status:", cardX + 6, contentY + 12);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.status, cardX + 28, contentY + 12);

        // Right column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Smart Card:", cardX + 95, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.smartCard, cardX + 120, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Marital Status:", cardX + 95, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.marital, cardX + 120, contentY + 6);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Birth Place:", cardX + 95, contentY + 12);
        doc.setFont(FONTS.secondary, 'normal');
        const birthPlaceText = doc.splitTextToSize(fields.birthPlace, 55);
        doc.text(birthPlaceText, cardX + 120, contentY + 12);

        // Note section if present
        if (fields.note) {
            doc.setDrawColor(...borderColor);
            doc.setLineWidth(0.3);
            doc.line(cardX + 6, contentY + 22, cardX + cardWidth - 6, contentY + 22);
            
            doc.setFontSize(8);
            doc.setFont(FONTS.secondary, 'italic');
            doc.setTextColor(100, 100, 100);
            const noteText = doc.splitTextToSize(fields.note, cardWidth - 16);
            doc.text(noteText, cardX + 6, contentY + 27);
        }

        y += 66;
    });

    // --- Disclaimer ---
    doc.addPage();
    const disclaimerX = 15;
    const disclaimerY = 230;
    const disclaimerWidth = 180;
    const disclaimerHeight = 40;

    doc.setFillColor(245, 246, 250);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.7);
    doc.roundedRect(disclaimerX, disclaimerY, disclaimerWidth, disclaimerHeight, 4, 4, 'FD');

    doc.setFontSize(11);
    doc.setFont(FONTS.primary, 'bold');
    doc.setTextColor(...COLORS.black);
    doc.text("Disclaimer & Verification Notice", disclaimerX + 8, disclaimerY + 12);

    doc.setFontSize(9);
    doc.setFont(FONTS.secondary, 'normal');
    doc.setTextColor(64, 64, 64);

    const disclaimerText =
        "This verification report is based on data received from the Department of Home Affairs and related data sources. " +
        "Information is presented as received and should be validated for compliance and official purposes.";
    const wrappedDisclaimer = doc.splitTextToSize(disclaimerText, disclaimerWidth - 10);
    doc.text(wrappedDisclaimer, disclaimerX + 8, disclaimerY + 20);

    addModernFooter(doc);
    doc.save("rsa_id_verification_summary.pdf");
}


// --- AML PDF Generator ---
export function generateAmlSummaryPdf(summaryData, date_from, date_to, skin = {}) {
    const doc = new jsPDF();
    let y = 58;
    const cardWidth = 175;
    const cardX = 17;

    // Colors and logo
    const accentColor = skin.primaryColor || [89, 255, 116];
    const headerBg = skin.headerBg || [255, 255, 255];
    const cardBg = skin.cardBg || [245, 246, 250];
    const borderColor = skin.borderColor || [200, 200, 200];
    const shadowColor = skin.shadowColor || [235, 235, 235];

    // --- Header with logo ---
    doc.setFillColor(...headerBg);
    doc.rect(0, 0, 210, 38, 'F');
    doc.setTextColor(...accentColor);
    doc.setFontSize(24);
    doc.setFont(FONTS.primary, 'bold');
    doc.text("AML Verification", 20, 22);

    // logo
    doc.addImage(
        require('@/assets/images/new_black_logo2.png'),
        'PNG',
        158, 8, 32, 14
    );

    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.7);
    doc.line(20, 30, 190, 30);

    doc.setFontSize(13);
    doc.setTextColor(64, 64, 64);
    doc.setFont(FONTS.secondary, 'bold');
    doc.text("Anti-Money Laundering Compliance Summary", 20, 36);

    // Date display
    const period = date_from && date_to
        ? `${formatDate(date_from)} – ${formatDate(date_to)}`
        : "All dates";
    doc.setFontSize(11);
    doc.setTextColor(128, 128, 128);
    doc.setFont(FONTS.secondary, 'normal');
    doc.text(`Report Period: ${period}`, 20, 44);
    doc.text(`Total Records: ${summaryData.length}`, 150, 44);

    // --- Summary Cards ---
    summaryData.forEach((item, idx) => {
        if (y > 230) {
            doc.addPage();
            y = 20;
        }

        // Extract applicant info
        const req = item.response?.RequestData || item.data?.RequestData || {};
        const fieldsData = item.response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes || {};
        const status = item.response?.Status || item.data?.Status || "N/A";
        const amlSummary = getAmlStatusSummary(fieldsData);

        const fields = {
            index: idx + 1,
            name: `${req.FirstName || ""} ${req.LastName || ""}`.trim() || "Name not provided",
            dob: req.DateOfDirth || req.DateOfBirth || "N/A",
            status: status,
            decision: amlSummary.decision,
            pep: amlSummary.pep.found ? `Found (${amlSummary.pep.count})` : "Not Found",
            nm: amlSummary.nm.found ? `Found (${amlSummary.nm.count})` : "Not Found",
            wls: amlSummary.wls.found ? `Found (${amlSummary.wls.count})` : "Not Found",
            note: item.response?.Message || item.data?.Message || ""
        };

        doc.setFillColor(...shadowColor);
        doc.roundedRect(cardX + 1, y + 1, cardWidth, 60, 4, 4, 'F');
        
        doc.setFillColor(...cardBg);
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(cardX, y, cardWidth, 60, 4, 4, 'FD');

        doc.setFillColor(...accentColor);
        doc.roundedRect(cardX, y, cardWidth, 10, 4, 4, 'F');
        doc.rect(cardX, y + 6, cardWidth, 4, 'F');

        doc.setFontSize(11);
        doc.setFont(FONTS.primary, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Applicant ${fields.index}: ${fields.name}`, cardX + 6, y + 7);

        // Content section
        const contentY = y + 16;
        doc.setFontSize(9);
        doc.setTextColor(64, 64, 64);

        // Left column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Date of Birth:", cardX + 6, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.dob, cardX + 30, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Status:", cardX + 6, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.status, cardX + 30, contentY + 6);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Decision:", cardX + 6, contentY + 12);
        doc.setFont(FONTS.secondary, 'normal');
        const decisionText = doc.splitTextToSize(fields.decision, 55);
        doc.text(decisionText, cardX + 30, contentY + 12);

        // Right column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("PEP:", cardX + 95, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.pep, cardX + 108, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("NM:", cardX + 95, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.nm, cardX + 108, contentY + 6);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("WLS:", cardX + 95, contentY + 12);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.wls, cardX + 108, contentY + 12);

        // Note section if present
        if (fields.note) {
            doc.setDrawColor(...borderColor);
            doc.setLineWidth(0.3);
            doc.line(cardX + 6, contentY + 22, cardX + cardWidth - 6, contentY + 22);
            
            doc.setFontSize(8);
            doc.setFont(FONTS.secondary, 'italic');
            doc.setTextColor(100, 100, 100);
            const noteText = doc.splitTextToSize(fields.note, cardWidth - 16);
            doc.text(noteText, cardX + 6, contentY + 27);
        }

        y += 66;
    });

    // --- Disclaimer section ---
    doc.addPage();
    const disclaimerX = 15;
    const disclaimerY = 230;
    const disclaimerWidth = 180;
    const disclaimerHeight = 40;

    doc.setFillColor(245, 246, 250);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.7);
    doc.roundedRect(disclaimerX, disclaimerY, disclaimerWidth, disclaimerHeight, 4, 4, 'FD');

    doc.setFontSize(11);
    doc.setFont(FONTS.primary, 'bold');
    doc.setTextColor(...COLORS.black);
    doc.text("Disclaimer & Compliance", disclaimerX + 8, disclaimerY + 12);

    doc.setFontSize(9);
    doc.setFont(FONTS.secondary, 'normal');
    doc.setTextColor(64, 64, 64);

    const disclaimerText =
        "This report contains data provided by an external service and has not been modified by THE GOOD PEOPLE DATA COMPANY (PTY) LTD. " +
        "All potential matches require thorough human review and verification. This report is for informational purposes only and should not be the sole basis for compliance or business decisions.";

    const wrappedDisclaimer = doc.splitTextToSize(disclaimerText, disclaimerWidth - 10);
    doc.text(wrappedDisclaimer, disclaimerX + 8, disclaimerY + 20);

    addModernFooter(doc);
    doc.save("aml_verification_summary.pdf");
}


// --- Refugee PDF Generator ---
export function generateRefugeeSummaryPdf(summaryData, date_from, date_to, skin = {}) {
    const doc = new jsPDF();
    let y = 58;
    const cardWidth = 175;
    const cardX = 17;

    const accentColor = skin.primaryColor || [89, 255, 116];
    const headerBg = skin.headerBg || [255, 255, 255];
    const cardBg = skin.cardBg || [245, 246, 250];
    const borderColor = skin.borderColor || [200, 200, 200];
    const shadowColor = skin.shadowColor || [235, 235, 235];

    // --- Header ---
    doc.setFillColor(...headerBg);
    doc.rect(0, 0, 210, 38, 'F');
    doc.setTextColor(...accentColor);
    doc.setFontSize(24);
    doc.setFont(FONTS.primary, 'bold');
    doc.text("Refugee Verification", 20, 22);

    doc.addImage(
        require('@/assets/images/new_black_logo2.png'),
        'PNG',
        158, 8, 32, 14
    );

    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.7);
    doc.line(20, 30, 190, 30);

    doc.setFontSize(13);
    doc.setTextColor(64, 64, 64);
    doc.setFont(FONTS.secondary, 'bold');
    doc.text("Refugee Status Documentation Summary", 20, 36);

    const period = date_from && date_to
        ? `${formatDate(date_from)} – ${formatDate(date_to)}`
        : "All dates";

    doc.setFontSize(11);
    doc.setTextColor(128, 128, 128);
    doc.setFont(FONTS.secondary, 'normal');
    doc.text(`Report Period: ${period}`, 20, 44);
    doc.text(`Total Records: ${summaryData.length}`, 150, 44);

    // --- Summary Cards ---
    summaryData.forEach((item, idx) => {
        if (y > 230) {
            doc.addPage();
            y = 20;
        }

        const res = item.data?.GoldenSource?.Results?.[0] || {};

        const fields = {
            index: idx + 1,
            name: `${res.FirstName || ""} ${res.Surname || ""}`.trim() || "Name not provided",
            dob: res.BirthDate || "N/A",
            status: res.Status || "N/A",
            fileNo: res.NIISFileNumber || res.AlternativeFileNumber || "N/A",
            nationality: res.Nationality || "N/A",
            gender: res.Gender || "N/A",
            note: item.data?.Message || ""
        };

        doc.setFillColor(...shadowColor);
        doc.roundedRect(cardX + 1, y + 1, cardWidth, 60, 4, 4, 'F');
        
        doc.setFillColor(...cardBg);
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(cardX, y, cardWidth, 60, 4, 4, 'FD');

        doc.setFillColor(...accentColor);
        doc.roundedRect(cardX, y, cardWidth, 10, 4, 4, 'F');
        doc.rect(cardX, y + 6, cardWidth, 4, 'F');

        doc.setFontSize(11);
        doc.setFont(FONTS.primary, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Applicant ${fields.index}: ${fields.name}`, cardX + 6, y + 7);

        // Content section
        const contentY = y + 16;
        doc.setFontSize(9);
        doc.setTextColor(64, 64, 64);

        // Left column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("File Number:", cardX + 6, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.fileNo, cardX + 30, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Date of Birth:", cardX + 6, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.dob, cardX + 30, contentY + 6);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Status:", cardX + 6, contentY + 12);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.status, cardX + 30, contentY + 12);

        // Right column
        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Nationality:", cardX + 95, contentY);
        doc.setFont(FONTS.secondary, 'normal');
        const nationalityText = doc.splitTextToSize(fields.nationality, 60);
        doc.text(nationalityText, cardX + 120, contentY);

        doc.setFont(FONTS.secondary, 'bold');
        doc.text("Gender:", cardX + 95, contentY + 6);
        doc.setFont(FONTS.secondary, 'normal');
        doc.text(fields.gender, cardX + 120, contentY + 6);

        // Note section if present
        if (fields.note) {
            doc.setDrawColor(...borderColor);
            doc.setLineWidth(0.3);
            doc.line(cardX + 6, contentY + 22, cardX + cardWidth - 6, contentY + 22);
            
            doc.setFontSize(8);
            doc.setFont(FONTS.secondary, 'italic');
            doc.setTextColor(100, 100, 100);
            const noteText = doc.splitTextToSize(fields.note, cardWidth - 16);
            doc.text(noteText, cardX + 6, contentY + 27);
        }

        y += 66;
    });

    // --- Disclaimer ---
    doc.addPage();
    const disclaimerX = 15;
    const disclaimerY = 230;
    const disclaimerWidth = 180;
    const disclaimerHeight = 40;

    doc.setFillColor(245, 246, 250);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.7);
    doc.roundedRect(disclaimerX, disclaimerY, disclaimerWidth, disclaimerHeight, 4, 4, 'FD');

    doc.setFontSize(11);
    doc.setFont(FONTS.primary, 'bold');
    doc.setTextColor(...COLORS.black);
    doc.text("Disclaimer & Verification Notice", disclaimerX + 8, disclaimerY + 12);

    doc.setFontSize(9);
    doc.setFont(FONTS.secondary, 'normal');
    doc.setTextColor(64, 64, 64);

    const disclaimerText =
        "This verification report is generated using data obtained from the Department of Home Affairs Refugee System. " +
        "The information presented is unaltered and should be used solely for legitimate verification purposes.";
    const wrappedDisclaimer = doc.splitTextToSize(disclaimerText, disclaimerWidth - 10);
    doc.text(wrappedDisclaimer, disclaimerX + 8, disclaimerY + 20);

    addModernFooter(doc);
    doc.save("refugee_verification_summary.pdf");
}

// --- Utility Functions ---
export function extractFilteredSummary(data, date_from, date_to) {
    return data.filter(item => {
        const itemDate = new Date(item.date);
        const from = date_from ? new Date(date_from + 'T00:00:00') : null;
        const to = date_to ? new Date(date_to + 'T23:59:59.999') : null;

        if (from && itemDate < from) return false;
        if (to && itemDate > to) return false;
        return true;
    });
}

function formatDate(dateString) {
    if (!dateString) return null;
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
        });
    } catch (e) {
        return dateString;
    }
}

function addModernFooter(doc) {
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text(`Page ${i} of ${pageCount}`, 15, 290);
        doc.text(`Generated on ${new Date().toLocaleString()}`, 195, 290, { align: "right" });
    }
}

export function getAmlStatusSummary(fieldsData) {
    const summary = {
        pep: { found: false, count: 0, highlights: [] },
        nm: { found: false, count: 0, highlights: [] },
        wls: { found: false, count: 0, highlights: [] },
        decision: fieldsData.DRWatchlistVerification?.WatchlistDecision || "N/A"
    };

    // PEP
    const pepResults = fieldsData.DSWatchlistVerification?.PepResults || [];
    if (pepResults.length > 0) {
        summary.pep.found = true;
        summary.pep.count = pepResults.length;
        summary.pep.highlights = pepResults.map(r => {
            if (Array.isArray(r.aliases) && r.aliases.length) {
                return r.aliases.join(', ');
            }
            return '';
        }).filter(Boolean);
    }

    // NM
    const nmResults = fieldsData.DSWatchlistVerification?.NmResults || [];
    if (nmResults.length > 0) {
        summary.nm.found = true;
        summary.nm.count = nmResults.length;
        summary.nm.highlights = nmResults.map(r => {
            if (Array.isArray(r.highlights) && r.highlights.length) {
                return r.highlights.join(' | ');
            }
            return '';
        }).filter(Boolean);
    }

    // WLS
    const wlsResults = fieldsData.DSWatchlistVerification?.WlsResults || [];
    if (wlsResults.length > 0) {
        summary.wls.found = true;
        summary.wls.count = wlsResults.length;
        summary.wls.highlights = wlsResults.map(r => {
            if (Array.isArray(r.highlights) && r.highlights.length) {
                return r.highlights.join(' | ');
            }
            return '';
        }).filter(Boolean);
    }

    return summary;
}

// --- Summary Mapping Functions ---
export function mapRsaIdSummary(data) {
    return (data || []).map((item, idx) => {
        const res = item?.data?.response || {};
        return {
            index: idx + 1,
            name: `${res.FirstName || ""} ${res.Surname || ""}`.trim() || "Name not provided",
            dob: res.BirthDate || "N/A",
            id: res.IdNumber || "N/A",
            status: res.Status || "N/A",
            marital: res.MaritalStatus || "N/A",
            smartCard: res.SmartCardIssued ? "Yes" : "No",
            birthPlace: res.BirthPlaceCountry || "N/A",
            issueDate: formatDate(res.IDIssueDate) || "N/A",
            note: res.Message || "",
            date: item.date_display || formatDate(item.date) || "",
        };
    });
}

export function mapAmlSummary(data) {
    return (data || []).map((item, idx) => {
        const req = item.response?.RequestData || item.data?.RequestData || {};
        const fieldsData = item.response?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes || {};
        const status = item.response?.Status || item.data?.Status || "N/A";
        const amlSummary = getAmlStatusSummary(fieldsData);

        return {
            index: idx + 1,
            name: `${req.FirstName || ""} ${req.LastName || ""}`.trim() || "Name not provided",
            dob: req.DateOfDirth || req.DateOfBirth || "N/A",
            status: status,
            decision: amlSummary.decision,
            pep: amlSummary.pep.found ? `Found (${amlSummary.pep.count})` : "Not Found",
            nm: amlSummary.nm.found ? `Found (${amlSummary.nm.count})` : "Not Found",
            wls: amlSummary.wls.found ? `Found (${amlSummary.wls.count})` : "Not Found",
            note: item.response?.Message || item.data?.Message || "",
            date: item.date_display || formatDate(item.date) || "",
        };
    });
}

export function mapRefugeeSummary(data) {
    return (data || []).map((item, idx) => {
        const res = item.data?.GoldenSource?.Results?.[0] || {};
        return {
            index: idx + 1,
            name: `${res.FirstName || ""} ${res.Surname || ""}`.trim() || "Name not provided",
            dob: res.BirthDate || "N/A",
            status: res.Status || "N/A",
            fileNo: res.NIISFileNumber || res.AlternativeFileNumber || "N/A",
            nationality: res.Nationality || "N/A",
            gender: res.Gender || "N/A",
            note: item.data?.Message || "",
            date: item.date_display || formatDate(item.date) || "",
        };
    });
}

export default {
    methods: {
        formatDate(date) {
            return formatDate(date);
        },
    }
};

export async function exportSummaryData(summary, format, filename = "Summary") {
    const current_date = new Date().toISOString().split("T")[0];
    if (format === "csv") {
        if (typeof window.Papa === "undefined") {
            await loadPapaParse();
        }
        const csv_content = window.Papa.unparse(summary);
        const blob = new Blob([csv_content], { type: "text/csv;charset=utf-8;" });
        downloadBlob(blob, `${filename}_${current_date}.csv`);
    } else if (format === "excel") {
        if (typeof window.XLSX === "undefined") {
            await loadXLSX();
        }
        const workbook = window.XLSX.utils.book_new();
        const worksheet = window.XLSX.utils.json_to_sheet(summary);
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
        window.XLSX.writeFile(workbook, `${filename}_${current_date}.xlsx`);
    } else if (format === "json") {
        const json_content = JSON.stringify(summary, null, 2);
        const blob = new Blob([json_content], { type: "application/json;charset=utf-8;" });
        downloadBlob(blob, `${filename}_${current_date}.json`);
    }
}

function downloadBlob(blob, filename) {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function loadPapaParse() {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js";
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

function loadXLSX() {
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

export function preprocessSummaryData(data) {
    return (data || []).map(item => ({
        ...item,
        date: item.date || item.date_created || item.date_display || ""
    }));
}

// --- Namibia Credit Report Summary Mapper ---
export function mapNamibiaCreditReportSummary(data) {
    return (data || []).map(item => {
        const report = item?.data?.data?.report || {};
        const individual = report?.individual || {};
        const general = individual?.general || {};
        const identifications = individual?.identifications || {};
        const parameters = report?.parameters || {};
        const dashboard = report?.dashboard || {};
        const cip = dashboard?.cip || {};
        const inquiries = dashboard?.inquiries || {};
        const disputes = dashboard?.disputes || {};
        const economicData = dashboard?.economicData || {};
        const judgements = report?.judgements || {};

        return {
            Date: item.date || "",
            ID_Number: identifications?.nationalID || parameters?.idNumber || "",
            ID_Type: parameters?.searchIdNumberType || parameters?.idNumberType || "",
            Surname: general?.presentSurname || "",
            First_Name: general?.forenameOrInitial1 || "",
            Gender: general?.gender || "",
            CIP_Score: cip?.score || "",
            CIP_Grade: cip?.grade || "",
            CIP_Trend: cip?.trend || "",
            Inquiries_Last_12_Months: inquiries?.inquiriesForLast12Months || 0,
            Subscribers_Last_12_Months: inquiries?.subscribersInLast12Months || 0,
            Active_Judgement_Disputes: disputes?.activeJudgementDisputes || 0,
            Number_Of_Judgements: economicData?.numberOfJudgements || judgements?.numberOfJudgements || 0,
            Total_Judgement_Amount: economicData?.totalAmountOfJudgements?.localValue || judgements?.totalAmountOfJudgements?.localValue || 0,
            Currency: economicData?.totalAmountOfJudgements?.currency || judgements?.totalAmountOfJudgements?.currency || "NAD",
            Request_Id: item?.data?.requestId || "",
            Request_Status: item?.data?.requestStatus || item?.data?.status || "",
            PDF_URL: item?.data?.data?.PDF_URL || ""
        };
    });
}